import React, { useEffect, useState, useRef } from "react";
import type { TableProps } from "antd";
import { ColumnsType, ColumnType } from "antd/es/table";
import {
  Form,
  Input,
  Upload,
  Popconfirm,
  Table,
  Typography as Typographys,
  Button as Buttons,
  Select,
  notification,
  Space,
  Tag,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import DocumentsController from "./Controller/document_controller.ts";
import BroadcastController from "./Controller/broadcast_controller.ts";
import Link from "@mui/material/Link";
import CustomSeparator from "./../../layouts/AdminLayout/Header/Breadcrumbs.js";
import Typography from "@mui/material/Typography";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { createStyles } from "antd-style";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
} from "@mui/material";
import { MdPublish } from "react-icons/md";
import placeholder from "../../images/placeholder.jpg";

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

const pdf_png = require("./../../images/pdf.png");
type NotificationType = "success" | "info" | "warning" | "error";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  key: string;
  userID: string;
  broadcastID: string;
  title: string;
  message: string;
  start_datetime: string;
  end_datetime: string;
  poster: string;
  publish_status: any;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "poster" | "publish_status";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  let inputNode;

  if (inputType === "poster") {
    inputNode = (
      <Upload beforeUpload={() => false} listType="picture">
        <Buttons color="default" variant="solid" icon={<UploadOutlined />}>
          Choose...
        </Buttons>
      </Upload>
    );
  } else if (inputType === "publish_status") {
    inputNode = (
      <Select value={record.publish_status}>
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else {
    inputNode = <Input />;
  }

  // File handler function
  const getFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList; // Return file list
  };

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[{ required: true, message: `Please input ${title}!` }]}
          valuePropName={inputType === "poster" ? dataIndex : "value"} // Set valuePropName conditionally
          getValueFromEvent={inputType === "poster" ? getFile : undefined} // Set getValueFromEvent conditionally
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ columns, onChange, checkedColumns }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // State for checkboxes, synchronized with `checkedColumns` from parent
  const [checked, setChecked] = useState(checkedColumns);

  useEffect(() => {
    setChecked(checkedColumns); // Synchronize state with prop
  }, [checkedColumns]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    onChange(newChecked); // Notify parent about changes
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {columns.map((value) => {
            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checked.includes(value.dataIndex)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString); // Convert to Date object
  return date.toLocaleString("en-US", {
    weekday: "long", // Example: Friday
    year: "numeric", // Example: 2024
    month: "long", // Example: November
    day: "numeric", // Example: 29
    hour: "numeric", // Example: 9
    minute: "numeric", // Example: 30
  });
};
const App: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();
  const userID = localStorage.getItem("userID");

  const openNotificationWithIcon = (
    type: NotificationType,
    desc: string,
    message: String
  ) => {
    api[type]({
      message: message,
      description: desc,
    });
  };

  const [form] = Form.useForm();
  const [rows, setRows] = useState<any[]>([]);
  const [data, setData] = useState<DataType[]>(rows);
  const [documentCategoryFilter, setDocumentCategoryFilters] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    BroadcastController.getBroadcasts().then((response) => {
      console.log(response);
      const rowData = response.map((item, index) => ({
        key: index,
        broadcastID: item.broadcastID,
        title: item.title,
        message: item.message,
        start_datetime: item.start_datetime,
        end_datetime: item.end_datetime,
        poster: item.poster,
        publish_status: item.publish,
      }));

      setRows(Array.from(rowData));
    });
  }, []);

  // Update data whenever rows changes
  useEffect(() => {
    setData(rows);
  }, [rows]);

  type ApiResponse = {
    data: any[];
    status: string;
  };
  const refreshData = ({ status, data }: ApiResponse) => {
    const rows = data.map((item: any, index: number) => ({
      key: index,
      broadcastID: item.broadcastID,
      title: item.title,
      message: item.message,
      start_datetime: item.start_datetime,
      end_datetime: item.end_datetime,
      poster: item.poster,
      publish_status: item.publish,
    }));

    setRows(rows);
  };
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => record.key === editingKey;

  const edit = (record: Partial<DataType> & { key: React.Key }) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const delete_record = (rowId: string, userID) => {
    const payload = new FormData();
    payload.append("broadcastID", rowId); // Append broadcast ID
    payload.append("userID", userID); // Append userID
    BroadcastController.deleteBroadcasts(payload).then((response) => {
      console.log(response);
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been deleted successfully!",
          "Success"
        );

        refreshData(response);
      }
    });
  };

  const publish = (rowId: string, userID) => {
    const payload = new FormData();
    payload.append("broadcastID", rowId); // Append broadcast ID
    payload.append("userID", userID); // Append broadcast ID
    // console.log("broadcastID", rowId);

    BroadcastController.publishBroadcasts(payload).then((response) => {
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Broadcast has been published successfully!",
          "Success"
        );
        // setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
        refreshData(response);
      }
    });
  };

  const save = async (key: React.Key, userID) => {
    try {
      const row = (await form.validateFields()) as DataType;

      const newData = [...data];
      const index = newData.findIndex((item) => {
        return key === item.broadcastID;
      });
      if (index > -1) {
        const item = newData[index];
        const broadcastID = item.broadcastID;

        const payload = new FormData();
        payload.append("broadcastID", broadcastID); // Append event ID
        payload.append("userID", userID); // Append event ID

        // Append form data fields (except the file)
        Object.keys(row).forEach((field) => {
          if (field !== "poster") {
            // Skip the file field
            payload.append(`data[${field}]`, row[field]); // Add form fields to payload
          }
        });

        // Append the file (if uploaded)
        if (row.poster && row.poster.length > 0) {
          const file = row.poster[0]["originFileObj"]; // Get file object
          payload.append("poster", file); // Add the file to the payload

          console.log(file);
        }

        BroadcastController.editBroadcasts(payload).then((response) => {
          console.log(response);

          if (response?.status === "success") {
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!",
              "Success"
            );
            // Handle successful response and update rows or UI
            refreshData(response);
          }
        });

        console.log(broadcastID);
        console.log(row);

        setData(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const columns = [
    {
      title: "ID",
      dataIndex: "broadcastID",
      key: "broadcastID",
    },
    {
      title: "Status",
      dataIndex: "publish_status",
      key: "publish_status",
      editable: true,
      render: (publish_status: any) => {
        if (publish_status === 1 || publish_status === '1') {
          return <Tag color="green">{"Published"}</Tag>;
        } else {
          return <Tag color="red">{"Unpublished"}</Tag>;
        }
      },
      filters: [
        { text: "Published", value: 1 },
        { text: "Unpublished", value: 0 },
      ],
      onFilter: (value, record) => record.publish_status === value,
    },
    {
      title: "Poster",
      dataIndex: "poster",
      key: "poster",
      editable: true,
      render: (text: any) => {
        return (
          <>
            {text && text.trim() ? (
              <img
                src={text}
                alt="Event Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            ) : (
              <img
                src={placeholder}
                alt="Event Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            )}
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      width: "15%",
      editable: true,
      ...getColumnSearchProps("title"), //IMPLEMENT SEARCH FILTER
    },
    {
      title: "Message",
      dataIndex: "message",
      editable: true,
      ...getColumnSearchProps("message"),
    },
    {
      title: "Start Date Time",
      dataIndex: "start_datetime",
      editable: true,
    },
    {
      title: "End Date Time",
      dataIndex: "end_datetime",
      editable: true,
    },
    {
      title: "Operation",
      dataIndex: "operation",
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return editable ? (
          <>
            <Buttons
              onClick={() => save(record.broadcastID, userID)}
              color="default"
              variant="solid"
              style={{ marginBottom: "10px" }}
            >
              Save
            </Buttons>
            <Buttons color="default" variant="outlined">
              <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                Cancel
              </Popconfirm>
            </Buttons>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "90%",
            }}
          >
            <Buttons
              color="default"
              variant="solid"
              icon={<EditOutlined />}
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              style={{ marginBottom: "10px" }}
            >
              Edit
            </Buttons>
            {record.publish_status === 0 && (
              <Buttons
                disabled={editingKey !== ""}
                icon={<MdPublish />}
                style={{ marginBottom: 7 }}
              >
                <Popconfirm
                  title="Sure to publish?"
                  onConfirm={() => publish(record.broadcastID, userID)}
                >
                  Publish
                </Popconfirm>
              </Buttons>
            )}
            <Buttons
              type="primary"
              danger
              icon={<DeleteOutlined />}
              disabled={editingKey !== "" || editingKey !== ""}
            >
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => delete_record(record.broadcastID, userID)}
              >
                Delete
              </Popconfirm>
            </Buttons>
          </div>
        );
      },
    },
  ];

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);

    // call api here
  };

  const [checkedColumns, setCheckedColumns] = useState([
    "publish_status",
    "poster",
    "title",
    "message",
    "start_datetime",
    "end_datetime",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  // Define your status options
  const statusOptions = [
    { label: "Publish", value: 1 },
    { label: "Unpublish", value: 0 },
    // Add more options as needed
  ];

  const mergedColumns = columns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => ({
          record,
          inputType:
            col.dataIndex === "poster"
              ? "poster"
              : col.dataIndex === "publish_status"
              ? "publish_status"
              : "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          options:
            col.dataIndex === "publish_status" ? statusOptions : undefined,
        }),
      };
    });

  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "100%", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />
          <CustomSeparator pageName="broadcasts" />
          {contextHolder}

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Broadcast Directory
          </Typography>
          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList
              columns={columns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                className={styles.customTable}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                dataSource={rows}
                columns={mergedColumns as ColumnType<DataType>[]}
                rowClassName="editable-row"
                pagination={{ onChange: cancel }}
                scroll={{ x: "80vh", y: 150 * 5 }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default App;
